import { defineStore } from 'pinia'
import { api, ApiError } from '@/lib/api'
import { playChime } from '@/lib/chime'
import { useToast } from '@/composables/useToast'
import { usePursuitsStore } from '@/stores/pursuits'
import { useNotificationsStore } from '@/stores/notifications'
import type { OwnershipRequest } from '@/lib/types'

/**
 * Ownership hand-offs, from both sides.
 *
 * Holds the requests I have to answer and the ones I raised. The countdown is
 * driven off a server-supplied clock offset rather than the browser's, because
 * a laptop a few minutes fast would show "expired" on a request that is still
 * open — or worse, the reverse.
 */
export const useOwnershipStore = defineStore('ownership', {
  state: () => ({
    requests: [] as OwnershipRequest[],
    loading: false,
    /**
     * My org_members.id — the value pursuits.assignee_member_id points at.
     * Null until the roster resolves; every getter below is empty while it is,
     * which is the safe direction (no prompt beats a prompt for the wrong
     * person).
     */
    myMemberId: null as string | null,
    /** serverNow - clientNow, in ms. Applied to every countdown. */
    clockSkewMs: 0,
    /** The grace window, from the server. The 10 here is only a first paint. */
    windowSecs: 600,
    /** Ticks once a second so countdowns re-render without per-card timers. */
    now: Date.now(),
    /** Ids we've already chimed for, so a refetch doesn't ring again. */
    announced: new Set<string>() as Set<string>,
  }),

  getters: {
    /** Waiting on me: I own the pursuit and haven't answered. */
    toDecide(state): OwnershipRequest[] {
      return state.requests.filter((r) => r.status === 'pending' && r.owner_member_id === state.myMemberId)
    },
    /** Mine that are still open — shown so I know the clock is running. */
    mineOpen(state): OwnershipRequest[] {
      return state.requests.filter((r) => r.status === 'pending' && r.requester_member_id === state.myMemberId)
    },
    /** Pursuit ids with an open request, so cards can show a marker. */
    pendingPursuitIds(state): Set<string> {
      return new Set(state.requests.filter((r) => r.status === 'pending').map((r) => r.pursuit_id))
    },
  },

  actions: {
    /** Set by whoever loads the org roster (the layout, on org change). */
    setMemberId(id: string | null) {
      this.myMemberId = id
    },

    async fetch(announce = false) {
      this.loading = true
      try {
        const res = await api.listOwnershipRequests()
        const before = new Set(this.requests.filter((r) => r.status === 'pending').map((r) => r.id))
        this.requests = res.requests ?? []
        // Authoritative, and free — no roster fetch to work out who I am.
        if (res.me_member_id) this.myMemberId = res.me_member_id
        if (res.server_time) {
          this.clockSkewMs = Date.parse(res.server_time) - Date.now()
        }
        if (res.window_secs) this.windowSecs = res.window_secs
        if (announce) this.announceNew(before)
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) this.requests = []
        // Otherwise keep what we had; the next SSE event or poll retries.
      } finally {
        this.loading = false
      }
    },

    /** Ring once per request that is newly waiting on me. */
    announceNew(before: Set<string>) {
      const fresh = this.toDecide.filter((r) => !before.has(r.id) && !this.announced.has(r.id))
      if (!fresh.length) return
      for (const r of fresh) this.announced.add(r.id)
      playChime()
    },

    /** Server clock, near enough for a countdown. */
    serverNow(): number {
      return this.now + this.clockSkewMs
    },

    /** Seconds left before a request grants itself; 0 once it's up. */
    secondsLeft(r: OwnershipRequest): number {
      return Math.max(0, Math.round((Date.parse(r.decide_by) - this.serverNow()) / 1000))
    },

    async request(pursuitId: string, note: string): Promise<boolean> {
      const toast = useToast()
      try {
        const res = await api.requestOwnership(pursuitId, note)
        this.requests = [res.request, ...this.requests.filter((r) => r.id !== res.request.id)]
        toast.success(
          `Asked ${res.request.owner_name || 'the owner'} — it becomes yours in ` +
            `${Math.round(this.secondsLeft(res.request) / 60)} minutes if they don't answer.`,
        )
        return true
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Could not send the request.')
        return false
      }
    },

    async decide(id: string, action: 'approve' | 'deny' | 'withdraw') {
      const toast = useToast()
      try {
        const res = await api.decideOwnership(id, action)
        const i = this.requests.findIndex((r) => r.id === id)
        if (i >= 0) this.requests[i] = res.request
        // The pursuit changed hands (or didn't) — the board and any open
        // pursuit page are both stale now.
        const pursuits = usePursuitsStore()
        if (pursuits.loaded) pursuits.fetchAll()
        useNotificationsStore().fetch()
        toast.success(outcomeMessage(res.request))
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Could not record that.')
        // Something else settled it (usually the clock) — resync.
        this.fetch()
      }
    },

    tick() {
      this.now = Date.now()
    },

    reset() {
      this.requests = []
      this.announced = new Set()
      this.myMemberId = null
    },
  },
})

function outcomeMessage(r: OwnershipRequest): string {
  switch (r.status) {
    case 'approved':
      return `Handed to ${r.requester_name}.`
    case 'expired':
      return `Time was already up — ${r.requester_name} has it.`
    case 'denied':
      return 'Kept. They have been told.'
    case 'withdrawn':
      return 'Request withdrawn.'
    case 'stale':
      return 'That pursuit had already moved to someone else.'
    default:
      return 'Done.'
  }
}
